/* tslint:disable:max-file-line-count */
import { action, computed, extendObservable, observable, runInAction, transaction } from 'mobx';

import { AnalyticsStore, ContextStore, ExperimentStore, SerializedData, Store } from 'bernie-core';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { PageData } from 'bernie-http';

import { BexApiSource } from 'sources/bex-api-source';
import { HotelSearchApiSource } from 'sources/hotel-search-api-source';
import { RateCalendarApiSource } from 'sources/rate-calendar-api-source';
import { SOURCE_EVENTS } from 'sources/events/source-events';

import {
  adaptFullPropertySearch,
  BffResponseRetrofitAdapter,
  bffResponseRetrofitAdapters,
  carouselAdapter,
  createMapFilterDisplayOptions,
  getPropertySearchInput,
  mapOmnitureData,
  mapPropertySearchResults,
  MigrationExperimentMapping,
  SearchMigrationAdapter,
  searchMigrationAdapters,
  searchMigrationAdaptorExecutor,
  searchMigrationExperimentMapping
} from 'stores/adapters';
import { MultiItemStore } from 'stores/multi-item-store';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  CarouselModel,
  CustomerInterest,
  HotelResultsViewModel,
  HotelViewModel,
  LoyaltyEducation,
  LoyaltyMessagingModel,
  OverFilteredModel,
  PrimaryPageMessagingModel,
  ReferrerInfoModel,
  RegionInfoModel,
  RewardsDialogModel,
  SharedGuidsModel
} from 'models';
import { ExitIntentModel } from '/models/common/common-types';
import { MultiItemContext } from 'models/multi-item/multi-item-types';
import { RateCalendarModel } from 'models/common/rate-calendar-model';
import { ResultType } from 'constants/resultType';

import { DateUtils } from 'utils/date-utils';

import { FullPropertySearch, PaginationInput, PropertyMapMigration, PropertySearch } from '__generated__/api';
import { handleBot } from './bot-header-parser';
import { OperationResult } from '__generated__/api/api-operations';

export class SearchResultsStore extends Store {
  private PAST_PADDING: number = 90;
  private FUTURE_PADDING: number = 150;
  public static INITIAL_HOTEL_RENDER_COUNT = 20;
  public static HOTEL_TOTAL_RENDER_COUNT = 50;

  constructor(
    private source: HotelSearchApiSource,
    private bexApiSource: BexApiSource,
    private rateCalendarSource: RateCalendarApiSource,
    state: SerializedData,
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);

    this.searchCriteria = new SearchCriteriaModel();

    this.fetch = this.fetch.bind(this);
    this.fetchAlternateDates = this.fetchAlternateDates.bind(this);
    this.fetchBexApiPropertySearch = this.fetchBexApiPropertySearch.bind(this);
    this.fetchFullBexApiPropertySearchTemp = this.fetchFullBexApiPropertySearchTemp.bind(this);
    this.fetchBexApiPropertyMapMigration = this.fetchBexApiPropertyMapMigration.bind(this);
  }

  public hydrate(data: SerializedData): void {
    delete data.source;
    delete data.bexApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    this.syncSearchDestination();
    delete data.searchCriteria;

    extendObservable(this, data);
  }

  public static DEFAULTS = {
    alternateDateFetchUrl: undefined,
    exitIntent: undefined,
    expandForm: false,
    foregoClientFetch: false,
    hotels: [],
    hotelsMaxDisplay: SearchResultsStore.INITIAL_HOTEL_RENDER_COUNT,
    insights: undefined,
    isLoading: true,
    loyaltyMessagingModel: undefined,
    messages: undefined,
    omnitureData: {},
    overFilteredModel: undefined,
    pageData: {},
    primaryPageMessagingModel: undefined,
    searchAndFilterDisplayOptions: {},
    searchedDestination: '',
    shouldFetch: true,
    sortAndFilter: {},
    totalPropertyCount: 0,
    universalDataObject: {},
    carouselListings: []
  };

  @observable
  public alternateDateFetchUrl: string;
  @observable
  public error: any;
  @observable
  public errors: any[];
  @observable
  public exitIntent: ExitIntentModel;
  @observable
  public expandForm: boolean; // Set on client.
  @observable
  public foregoClientFetch: boolean;
  @observable
  public hotels: HotelViewModel[];
  @observable
  public hotelsMaxDisplay: number;
  @observable
  public insights: any;
  @observable
  public isLoading: boolean;
  @observable
  public loyaltyEducation: LoyaltyEducation;
  @observable
  public loyaltyMessagingModel: LoyaltyMessagingModel;
  @observable
  public map: any;
  @observable
  public messages: any;
  @observable
  public omnitureData: any;
  @observable
  public overFiltered: boolean;
  @observable
  public overFilteredModel: OverFilteredModel;
  @observable
  public primaryPageMessagingModel: PrimaryPageMessagingModel;
  @observable
  public propertyTypeCarousel: CarouselModel;
  @observable
  public rates: RateCalendarModel;
  @observable
  public referrerInfo: ReferrerInfoModel;
  @observable
  public regionInfo: RegionInfoModel;
  @observable
  public rewardsDialogContent: RewardsDialogModel;
  @observable
  public searchAndFilterDisplayOptions: any; // Set on server.
  @observable
  public searchedDestination: string;
  @observable
  public sharedGuids: SharedGuidsModel;
  @observable
  public shouldFetch: boolean;
  @observable
  public sortAndFilter: any;
  @observable
  public summaryMessages: PropertySearch.ResultsSummary[];
  @observable
  public totalPropertyCount: number;
  @observable
  public universalDataObject: any;
  @observable
  public carouselListings: CarouselModel[];

  @observable.ref
  public pageData: PageData;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  public defaultRequestFields: any;
  public bexApi: number = 0;
  public maxHotelsDisplayedPerLoad: number = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;

  @computed
  get isError(): boolean {
    return Boolean(!this.isLoading && (this.error || this.errors || this.overFiltered));
  }

  @computed
  get isEmpty(): boolean {
    return Boolean(!this.isLoading && (!this.hotels || this.hotels.length === 0));
  }

  @action
  public showMoreHotels(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItem: MultiItemStore
  ): Promise<number> {
    this.isLoading = true;

    return new Promise(resolve => {
      setTimeout(
        action(() => {
          const currentMax = this.hotelsMaxDisplay;
          const numHotelsFetched = this.filterHotels(this.hotels, multiItem).length;

          if (this.hotelsMaxDisplay < numHotelsFetched) {
            this.hotelsMaxDisplay += this.maxHotelsDisplayedPerLoad;
          }

          if (
            this.hotelsMaxDisplay + this.maxHotelsDisplayedPerLoad >= numHotelsFetched && // is one batch away from the end
            numHotelsFetched < this.totalPropertyCount
          ) {
            this.fetchNextBatch(analyticsStore, context, customerInterests, numHotelsFetched, this.maxHotelsDisplayedPerLoad, multiItem);
          } else {
            this.isLoading = false;
          }

          resolve(currentMax);
        }),
        400
      );
    });
  }

  // Temporary helper function to exclude non-hotels returned by BFF from hotel list
  private filterHotels(hotels, multiItem: MultiItemStore) {
    if (hotels === null || typeof this.getFullPropertySearchQuery(multiItem) === 'function') {
      return hotels;
    }

    return hotels.filter(hotel => hotel.type === 'HotelViewModel');
  }

  private fetchNextBatch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    startingIndex: number,
    numToFetch: number,
    multiItem: MultiItemStore
  ): void {
    const fullSearchQuery = this.getFullPropertySearchQuery(multiItem);

    if (typeof fullSearchQuery === 'function') {
      const multiItemContext = multiItem ? multiItem.multiItemContext : null;

      fullSearchQuery(context, customerInterests, multiItemContext, { size: numToFetch, startingIndex })
        .then(({ data, extensions }) => {
          transaction(() => {
            // isLoading needs to change with new omniture data in one step
            this.isLoading = false;
            this.hotels = this.hotels.concat(mapPropertySearchResults(data));
            this.omnitureData = mapOmnitureData(extensions.analytics);

            if (multiItem) {
              multiItem.updateSessionId(this.getMultiItemId(data));
            }
          });
        })
        .catch(error => {
          this.isLoading = false;
          this.logger.logEvent(STORE_EVENTS.BEX_API_SEARCH_RESULTS_STORE_FETCH_ERROR, getErrorString(error));
        });
    } else {
      const criteria = SearchQueryAdapter.parseOut({
        ...this.searchCriteria.toJSON(),
        count: numToFetch,
        startingIndex
      });

      this.source
        .fetch(context, criteria)
        .then(data => {
          if (data.hotels) {
            transaction(() => {
              // isLoading needs to change with new omniture data in one step
              this.hotels = this.hotels.concat(this.filterHotels(data.hotels, multiItem));
              this.omnitureData = data.omnitureData;
              this.isLoading = false;
            });
          } else {
            this.isLoading = false;
          }
        })
        .catch(error => {
          this.isLoading = false;
          this.logErrorEvent(error);
        });
    }

    analyticsStore.publishClientSidePayload([this.omnitureData]);
  }

  @action
  public updateSearchCriteria(searchCriteria): void {
    this.searchCriteria = searchCriteria;
    this.propertyTypeCarousel = undefined;
    this.syncSearchDestination();
    this.shouldFetch = true;
  }

  @action
  public forceFetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    experiments: ExperimentStore,
    additionalCriteria: any = {},
    multiItem: MultiItemStore
  ): Promise<any> {
    this.shouldFetch = true;

    return this.fetch(analyticsStore, context, [], experiments, additionalCriteria, multiItem);
  }

  public prepareFetch(
    context: ContextStore,
    searchCriteria: SearchCriteriaModel,
    additionalCriteria: SerializedData,
    experiments: ExperimentStore,
    customerInterests: CustomerInterest[],
    multiItem: MultiItemStore,
    headers?: SerializedData
  ): {
    promises: Array<Promise<any>>;
    migrationAdapters: SearchMigrationAdapter[];
    migrationExperimentMapping: MigrationExperimentMapping;
    retrofitAdapters: BffResponseRetrofitAdapter[];
  } {
    const promises: Array<Promise<any>> = new Array(2);
    const multiItemContext = multiItem ? multiItem.multiItemContext : null;

    const criteriaJson = searchCriteria && searchCriteria.toJSON();
    const combinedCriteria = { ...SearchQueryAdapter.parseOut(criteriaJson), ...additionalCriteria };
    const bexApiCriteria = { ...criteriaJson, ...additionalCriteria };

    const propertyMapMigration = experiments.exposures.shopping_pwa_mm_bex_api_lodging_map_and_properties;

    handleBot(headers, () => {
      if (propertyMapMigration && propertyMapMigration.bucket !== 0) {
        propertyMapMigration.bucket = 0;

        this.logger.logEvent(SOURCE_EVENTS.BEX_API_LODGING_BOT_REBUCKET_EVENT);
      }
    });

    const retrofitAdapters: BffResponseRetrofitAdapter[] = bffResponseRetrofitAdapters;

    let migrationAdapters: SearchMigrationAdapter[] = searchMigrationAdapters;
    let migrationExperimentMapping: MigrationExperimentMapping = {};

    if (multiItem && multiItem.isPackageShoppingPath()) {
      migrationAdapters = [adaptFullPropertySearch];

      promises[1] = this.fetchFullBexApiPropertySearchTemp(context, customerInterests, multiItemContext, bexApiCriteria);
    } else if (propertyMapMigration && (propertyMapMigration.bucket === 1 || propertyMapMigration.bucket === 2)) {
      migrationExperimentMapping = searchMigrationExperimentMapping;

      promises[0] = this.source.fetch(context, combinedCriteria);
      promises[1] = this.fetchBexApiPropertyMapMigration(context, customerInterests, multiItemContext, bexApiCriteria);
    } else {
      promises[0] = this.source.fetch(context, combinedCriteria);
      promises[1] = this.fetchBexApiPropertySearch(context, customerInterests, multiItemContext, bexApiCriteria);
    }

    return { promises, migrationAdapters, migrationExperimentMapping, retrofitAdapters };
  }

  private getFullPropertySearchQuery(multiItem: MultiItemStore) {
    if (multiItem && multiItem.isPackageShoppingPath()) {
      return this.fetchFullBexApiPropertySearchTemp;
    }
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    customerInterests: CustomerInterest[],
    experiments: ExperimentStore,
    additionalCriteria: SerializedData = {},
    multiItem: MultiItemStore,
    headers?: SerializedData
  ): Promise<any> {
    if (this.shouldFetch === false) {
      return Promise.resolve(this);
    }

    this.shouldFetch = false;
    this.isLoading = true;
    this.error = null;
    this.hotels = [];
    this.insights = undefined;
    this.messages = undefined;
    this.carouselListings = [];

    const { promises, migrationAdapters, migrationExperimentMapping, retrofitAdapters } = this.prepareFetch(
      context,
      this.searchCriteria,
      additionalCriteria,
      experiments,
      customerInterests,
      multiItem,
      headers
    );

    return Promise.all(promises)
      .then(([bffResults, bexApiResults]) => {
        if (multiItem && bexApiResults) {
          multiItem.updateSessionId(this.getMultiItemId(bexApiResults.data));
        }

        return searchMigrationAdaptorExecutor(
          context,
          this.pageData,
          migrationAdapters,
          migrationExperimentMapping,
          retrofitAdapters,
          experiments,
          bffResults,
          bexApiResults,
          this.logger
        );
      })
      .then((data: HotelResultsViewModel) =>
        runInAction('successfully updating search results', () => {
          this.isLoading = false;

          if (data) {
            if (!data.hotels) {
              data.hotels = null;
            }

            this.updateCarouselListings(data);
            this.overrideBffMapFilters(data);
            this.setOverFilteredModel(data.overFilteredModel);

            const searchCriteria = this.searchCriteria.clone();
            const defaultRequestFields = data.defaultRequestFields;

            if (defaultRequestFields) {
              Object.assign(searchCriteria, defaultRequestFields);
              //hack to prevent the destination in the url from getting updated. This causes the back button to not work correctly
              delete defaultRequestFields.destination;
            }

            const sortAndFilterSelected = data.sortAndFilter && data.sortAndFilter.selected;
            if (sortAndFilterSelected) {
              Object.assign(searchCriteria, sortAndFilterSelected);
            }

            if (data.exitIntent) {
              this.exitIntent = data.exitIntent;
            }

            this.searchCriteria = searchCriteria;

            Object.assign(this, data);
            this.setIntentMediaParams(data.intentMedia);

            analyticsStore.publishClientSidePayload([this.omnitureData]);
          }

          return data;
        })
      )
      .catch(err =>
        runInAction('error during search results fetch', () => {
          this.logErrorEvent(err);
          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;
          this.isLoading = false;
          this.shouldFetch = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  @action
  public fetchAlternateDates(context, additionalCriteria: any = {}): Promise<any> {
    const promise = this.source.fetchAlternateDates(context, additionalCriteria);

    return promise
      .then(data =>
        runInAction('successfully updating alternate date on search results', () => {
          if (data && this.hotels) {
            this.hotels.forEach((hotel, index) => {
              if (data[hotel.id]) {
                this.hotels[index] = Object.assign({}, hotel, { alternateDates: data[hotel.id] });
              }
            });
          }

          return data;
        })
      )
      .catch(err =>
        runInAction('error during alternate date on search results fetch', () => {
          this.logErrorEvent(err);

          // don't assign the error to this.error, as errors in this catch are technical and NOT user friendly
          this.error = true;

          // recover and pass object with error details
          return {
            error: err
          };
        })
      );
  }

  public fetchHSRRates(l10n, requestStartDate, requestEndDate, adults, children, regionId, context) {
    const { startDate, endDate } = this.resolveSearchDates(l10n, requestStartDate, requestEndDate);

    return this.rateCalendarSource
      .fetchHSR(context, { startDate, endDate, adults, children }, regionId)
      .then(res => {
        if (res && res.rateCalendar) {
          this.rates = res.rateCalendar;
        } else {
          this.rates = {};
        }

        return this.rates;
      })
      .catch(error => {
        runInAction('rate calendar call failure', () => {
          this.logErrorEvent(error);
        });
      });
  }

  @action
  public clearRates() {
    this.rates = {};
  }

  private resolveSearchDates(l10n, requestStartDate, requestEndDate) {
    const dateUtils = new DateUtils(l10n);

    const today = dateUtils.getToday();
    const endDate = dateUtils.getNDaysFrom(requestEndDate, this.FUTURE_PADDING);
    const daysBetweenTodayAndRequestStartDate = dateUtils.getDaysBetween(today, requestStartDate);
    let startDate;

    if (daysBetweenTodayAndRequestStartDate <= this.PAST_PADDING && daysBetweenTodayAndRequestStartDate >= 0) {
      startDate = today;
    } else {
      startDate = dateUtils.getNDaysFrom(requestStartDate, -this.PAST_PADDING);
    }

    return { startDate, endDate };
  }

  private fetchBexApiPropertySearch(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext,
    combinedCriteria: any
  ): Promise<OperationResult<PropertySearch.Query>> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      combinedCriteria,
      this.sortAndFilter,
      multiItemContext
    );

    return this.bexApiSource.fetchBexApiPropertySearch(variables).then(({ data, extensions }) => ({ data, extensions }));
  }

  private fetchBexApiPropertyMapMigration(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext,
    combinedCriteria: any
  ): Promise<OperationResult<PropertyMapMigration.Query>> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      combinedCriteria,
      this.sortAndFilter,
      multiItemContext
    );

    return this.bexApiSource.fetchBexApiPropertyMapMigration(variables).then(({ data, extensions }) => ({ data, extensions }));
  }

  private fetchFullBexApiPropertySearchTemp(
    context: ContextStore,
    customerInterests: CustomerInterest[],
    multiItemContext: MultiItemContext,
    combinedCriteria: any,
    searchPagination?: PaginationInput
  ): Promise<OperationResult<FullPropertySearch.Query>> {
    const variables: PropertySearch.Variables = getPropertySearchInput(
      context,
      customerInterests,
      combinedCriteria,
      this.sortAndFilter,
      multiItemContext
    );
    variables.searchPagination = searchPagination;

    return this.bexApiSource.fetchFullBexApiPropertySearchTemp(variables).then(({ data, extensions }) => ({ data, extensions }));
  }

  private syncSearchDestination() {
    if (this.searchCriteria.destination && this.searchCriteria.destination !== '') {
      this.searchedDestination = this.searchCriteria.destination;
    } else if (this.searchedDestination && this.searchedDestination !== '') {
      this.searchCriteria.destination = this.searchedDestination;
    }
  }

  private logErrorEvent(err) {
    if (this.logger) {
      this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));
    }
  }

  private setOverFilteredModel(overFilteredModel: OverFilteredModel) {
    if (this.overFilteredModel && !overFilteredModel) {
      this.overFilteredModel = null;
    }
  }

  public findHotel(hotelId: string): HotelViewModel {
    return this.hotels.find(h => h.id && h.id.toString() === hotelId);
  }

  public setIntentMediaParams(intentMedia) {
    if (intentMedia) {
      const {
        city,
        countryCode,
        machineId,
        machineIdHashed,
        publisherSessionId,
        publisherSessionIdHashed,
        publisherUserIdHashed,
        referrerChannel,
        referrerSource,
        state
      } = intentMedia;
      this.sharedGuids = { machineId, machineIdHashed, publisherSessionId, publisherSessionIdHashed, publisherUserIdHashed };
      this.regionInfo = { city, state, countryCode };
      this.referrerInfo = { referrerChannel, referrerSource };
    }
  }

  @action
  public displaySearchButton() {
    this.map = Object.assign({}, this.map, {
      showSearchButton: true
    });
  }

  @action
  public dismissMapMessage() {
    this.map = Object.assign({}, this.map, {
      mapMessage: ''
    });
  }

  @action
  public clearDefaultRequestFields() {
    this.defaultRequestFields = {};
  }

  @action
  public setHotelsMaxDisplayToTotalRenderCount() {
    this.hotelsMaxDisplay = SearchResultsStore.HOTEL_TOTAL_RENDER_COUNT;
  }

  private updateCarouselListings(data: HotelResultsViewModel): void {
    if (data.propertyTypeCarousel) {
      this.propertyTypeCarousel = carouselAdapter.adapt(data.propertyTypeCarousel);
      this.carouselListings.push(this.propertyTypeCarousel);
    }
  }

  private overrideBffMapFilters(data: HotelResultsViewModel): void {
    // interim solution until map migrated to BEX
    // need to make sure sortAndFilter and mapFilters are in sync
    // and both use displayFields from search-sort-filter-adapter.ts
    if (data && data.map && data.map.filterDisplayOptions) {
      data.map.filterDisplayOptions = createMapFilterDisplayOptions();
    }
  }

  // This method is used to mold non-bex api input into bex api input.
  // Can be removed once bex api is 100%
  public splitListings(listings, messages) {
    let hotelListings = [];
    let messageListings = [];
    if (messages && messages.length > 0) {
      hotelListings = listings;
      messageListings = messages;
    } else if (listings != null) {
      listings.forEach((listing, index) => {
        if (listing.resultType === ResultType.MESSAGING) {
          messageListings.push({ ...listing, index });
        } else {
          hotelListings.push(listing);
        }
      });
    }

    return { hotelListings, messageListings };
  }

  private getMultiItemId(data: FullPropertySearch.Query): string {
    return (
      data &&
      data.propertySearch &&
      data.propertySearch.shoppingContext &&
      data.propertySearch.shoppingContext.multiItem &&
      data.propertySearch.shoppingContext.multiItem.id
    );
  }
}
